import axios from "axios";
import { API_BASE_URL, API_TOKEN, BRAND } from "../constants";

class ApiService {
  constructor() {
    // Attach token at request time (runs after localStorage is populated)
    axios.interceptors.request.use(
      (config) => {
        const skipAuth = config.headers?.["X-Skip-Auth"];
        if (skipAuth) return config;

        try {
          const token =
            localStorage.getItem("access_token") || API_TOKEN || null;
          if (token) {
            config.headers = config.headers || {};
            if (!config.headers["Authorization"]) {
              config.headers["Authorization"] = `Bearer ${token}`;
            }
          }
        } catch (e) {
          // ignore storage errors
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    axios.interceptors.response.use(
      (response) => {
        // handle new token (always overwrite if returned)
        let token =
          response.data?.access_token ||
          response.data?.new_token ||
          response.data?.data?.access_token ||
          response.data?.data?.new_token;
        if (typeof token === "string" && token.trim() !== "") {
          localStorage.setItem("access_token", token);
        }

        // skip session-expired handling when header set or global flag present
        const skip =
          response.config?.headers?.["X-Skip-Session-Check"] ||
          window.__skipSessionNotice === true;

        if (!skip && response.data?.redirect_login === true) {
          window.dispatchEvent(
            new CustomEvent("session-expired", {
              detail:
                response.data?.message || "Session expired, please login again",
            }),
          );
        }
        return response;
      },
      (error) => {
        const skip =
          error.config?.headers?.["X-Skip-Session-Check"] ||
          window.__skipSessionNotice === true;

        if (
          !skip &&
          error.response &&
          error.response.data?.redirect_login === true
        ) {
          window.dispatchEvent(
            new CustomEvent("session-expired", {
              detail:
                error.response.data?.message ||
                "Session expired, please login again",
            }),
          );
        }
        return Promise.reject(error);
      },
    );
  }
  //=================API for Register Page=================
  async register(formData) {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/register/register`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }
  //================API for Register Page END=================

  //=================API for Login Page=================
  async getProfile() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/profile/getProfile`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      return { status: false, message: "Failed to fetch profile" };
    }
  }

  async login({ company_email, username, password, pid, session_key }) {
    if (pid) {
      // salesmanLogin - do NOT send custom header (CORS preflight blocked previously)
      const form = new URLSearchParams();
      form.append("pid", pid);
      const response = await axios.post(
        `${API_BASE_URL}api/customer_backend/login/salesman_login`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } else if (session_key) {
      // session_key SSO login -> post session_key to same endpoint
      const form = new URLSearchParams();
      form.append("session_key", session_key);
      const response = await axios.post(
        `${API_BASE_URL}api/customer_backend/login/get_token_frontend`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } else {
      // normal login
      const form = new URLSearchParams();
      form.append("company_email", company_email);
      form.append("username", username);
      form.append("password", password);
      const response = await axios.post(
        `${API_BASE_URL}api/customer_backend/login/get_token_frontend`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    }
  }

  // logout by session_key (backend endpoint)
  async logout(session_key) {
    const form = new URLSearchParams();
    form.append("session_key", session_key || "");
    const response = await axios.post(
      `${API_BASE_URL}api/customer_backend/login/logout`,
      form,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async validateLoginEmail(company_email) {
    const response = await axios.post(
      `${API_BASE_URL}api/customer_backend/login/email_validation`,
      {
        company_email: company_email,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getLoginBackground() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/customer_backend/login/getLoginBackground`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching login background:", error);
      return { status: false, message: "Failed to fetch login background" };
    }
  }

  async customerForgot(email) {
    try {
      const form = new URLSearchParams();
      form.append("email", email);
      const response = await axios.post(
        `${API_BASE_URL}api/customer_backend/login/customerForgot`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending forgot password email:", error);
      return { status: false, message: "Failed to send email" };
    }
  }

  async resetPassword(email, validation, password) {
    const form = new URLSearchParams();
    form.append("email", email);
    form.append("validation", validation);
    form.append("password", password);
    const response = await axios.post(
      `${API_BASE_URL}api/customer_backend/login/resetPassword`,
      form,
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
    );
    return response.data;
  }

  async accountValidation(token) {
    const form = new URLSearchParams();
    form.append("token", token);
    const response = await axios.post(
      `${API_BASE_URL}api/customer_backend/login/accountValidation`,
      form,
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
    );
    return response.data;
  }

  async getUnsuccessfulAndOverdueTotal() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/Payment/getUnsuccessfulAndOverdueTotal`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getSwitchUsers(salesmanId, excludeCustomerId = "") {
    try {
      const form = new URLSearchParams();
      form.append("salesman_id", salesmanId || "");
      if (excludeCustomerId)
        form.append("exclude_customer_id", excludeCustomerId);
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getSwitchUsers`,
        form,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err) {
      console.error("getSwitchUsers error", err);
      return { status: 500, data: { users: [] } };
    }
  }

  //=================API for Login Page END=================

  //=================API for Product Detail Page=================
  async getItemDetail(item_id, region = "") {
    const form = new URLSearchParams();
    form.append("item_id", item_id);
    if (region) form.append("region", region);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getItemDetail`,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getInstockProduct(filters = {}) {
    const params = new URLSearchParams();

    if (filters.brand_id) params.append("brand_id", filters.brand_id);
    if (filters.category_code)
      params.append("category_code", filters.category_code);
    if (filters.product_id) params.append("product_id", filters.product_id);
    if (filters.attribute_id)
      params.append("attribute_id", filters.attribute_id);
    if (filters.search) params.append("search", filters.search);
    if (filters.filter_type) params.append("filter_type", filters.filter_type);
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.include_filters !== undefined)
      params.append("include_filters", filters.include_filters);
    if (filters.stock_filter !== undefined)
      params.append("stock_filter", filters.stock_filter);
    if (filters.sort !== undefined) params.append("sort", filters.sort);
    if (filters.discontinued_only !== undefined)
      params.append("discontinued_only", filters.discontinued_only);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getInstockProduct`,
      params,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getYouMayAlsoLikeItems(params = {}) {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getInstockProduct`,
      {
        product_code: params.product_code,
        page: params.page || 1,
        limit: params.limit || 12,
      },
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  // fetch top product groups for home (limited to 6)
  async getTopSixProduct() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getTopSixProduct`,
        {},
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err) {
      console.error("getTopSixProduct error", err);
      return { status: 500, data: { product: [] } };
    }
  }

  // fetch top sales (fast moving) products
  async getTopSalesProduct() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getTopSalesProduct`,
        {},
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err) {
      console.error("getTopSalesProduct error", err);
      return { status: 500, data: [] };
    }
  }

  async comparelist(params) {
    const formData = new URLSearchParams();

    // Support calling with:
    // - string (legacy ids) => treat as item_id
    // - { item_id, package_id } => prefer package_id when present (backend expects either one)
    if (typeof params === "string" || params == null) {
      formData.append("item_id", params || "");
    } else if (typeof params === "object") {
      const itemIds = params.item_id ?? "";
      const pkgIds = params.package_id ?? "";

      // If package_id present, send package_id only (backend requires either item_id OR package_id)
      if (pkgIds && String(pkgIds).trim() !== "") {
        formData.append("package_id", pkgIds);
      } else {
        formData.append("item_id", itemIds);
      }
    }

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/comparelist`,
      formData,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getPurchaseHistory(item_id, branch_id, pricing_type, bundle_id) {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getPurchaseHistory`,
      {
        item_id,
        branch_id,
        pricing_type,
        bundle_id,
      },
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data?.data || [];
  }

  async virtualStockChecking(item_id, order_quantity) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("item_id", item_id);
    form.append("order_quantity", order_quantity);
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/product/virtualStockChecking`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error checking virtual stock:", error);
      return { status: false, message: "Failed to check virtual stock" };
    }
  }

  async getTermMessage(item_id) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("item_id", item_id);
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/product/getTermMessage`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching term message:", error);
      return { status: false, message: "Failed to fetch term message" };
    }
  }

  async getBundleItems(filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append("search", filters.search);
    if (filters.page) params.append("page", filters.page);
    if (filters.brand_id) params.append("brand_id", filters.brand_id);
    if (filters.product_id) params.append("product_id", filters.product_id);
    if (filters.attribute_id)
      params.append("attribute_id", filters.attribute_id);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getBundleProduct`,
      params,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getPackageList(filters = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.append("search", filters.search);
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.brand_id) params.append("brand_id", filters.brand_id);
    if (filters.product_id) params.append("product_id", filters.product_id);
    if (filters.attribute_id)
      params.append("attribute_id", filters.attribute_id);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/get_package_list`,
      params,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getBundleDetail(bundle_id, region = "") {
    const form = new URLSearchParams();
    form.append("bundle_id", bundle_id);
    if (region) form.append("region", region);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getBundleDetail`,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getPackageDetail(package_id, region = "") {
    const form = new URLSearchParams();
    form.append("package_id", package_id);
    if (region) form.append("region", region);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getPackageDetail`,
      form,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async calculatePackagePrice(params) {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/calculatePackagePrice`,
      new URLSearchParams(params),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getPromotionSelection(item_id, region, qty) {
    const params = new URLSearchParams();
    params.append("item_id", item_id);
    params.append("region", region);
    params.append("qty", qty);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/product/getPromotionSelection`,
      params,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //=================API for Product Detail Page END==================

  //================API for Cart Page=================
  async getPricingType() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/getPricingType`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pricing types:", error);
      return { status: false, message: "Failed to fetch pricing types" };
    }
  }

  async checkFromCart(id, pricing_type) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("id", id);
    form.append("pricing_type", pricing_type);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/checkCartItem`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error checking item in cart:", error);
      return { status: false, message: "Failed to check item in cart" };
    }
  }

  async getMyCart(
    page = 1,
    pricingType,
    limit = 5,
    search = "",
    cartIds,
    csIds,
  ) {
    try {
      const form = new URLSearchParams();
      form.append("page", page);
      form.append("pricing_type", pricingType);
      form.append("limit", limit);

      if (search && String(search).trim() !== "") {
        form.append("search", String(search).trim());
      }

      form.append("cart_id", cartIds || "");
      form.append("cs_ids", csIds || "");

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/getMyCart`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching cart:", error);
      return { status: false, message: "Failed to fetch cart" };
    }
  }

  async getCheckoutDetail(selectedIds, pricingType, salesmanID, selectedCsIds) {
    try {
      const form = new URLSearchParams();
      form.append("selected_id", selectedIds);
      form.append("pricing_type", pricingType);
      form.append("salesman_id", salesmanID);
      form.append("cs_ids", selectedCsIds);
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/getCheckoutDetail`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching checkout details:", error);
      return { status: false, message: "Failed to fetch checkout details" };
    }
  }

  async checkSameSupplier(
    selectedItems,
    selectedCsIds,
    pricingType,
    shippingType,
  ) {
    try {
      const form = new URLSearchParams();
      form.append("cart_ids", selectedItems || "");
      form.append("cs_ids", selectedCsIds || "");
      form.append("pricing_type", pricingType || "");
      form.append("shipping_type", shippingType || "");
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/checkSameSupplier`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error checking same supplier:", error);
      return { status: false, message: "Failed to check supplier" };
    }
  }

  async getStaffList(salesmanID) {
    try {
      const form = new URLSearchParams();
      form.append("salesman_id", salesmanID);

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/selectStaff`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data || { status: 400, message: "No response data" };
    } catch (error) {
      console.error("Error fetching staff list:", error);
      return { status: 400, message: "Failed to fetch staff list" };
    }
  }

  async processCheckout(
    selectedItems,
    paymentType,
    pricingType,
    checkoutMessage = "",
    customerPO = "",
    customerPOdate = "",
    specifiedShippingDate = "",
    salesmanOpt = {},
    option = {},
    extraEinvoiceData = {},
    selectedCsIds,
    tinNo = "",
  ) {
    try {
      const form = new URLSearchParams();
      form.append("available_id", selectedItems);
      form.append("payment_type", paymentType);
      form.append("checkout_message", checkoutMessage);
      form.append("customer_po", customerPO);
      form.append("customer_po_date", customerPOdate);
      form.append("pricing_type", pricingType);
      if (specifiedShippingDate) {
        form.append("specified_shipping_date", specifiedShippingDate);
      }
      if (salesmanOpt && Object.keys(salesmanOpt).length > 0) {
        form.append("salesman_opt", JSON.stringify(salesmanOpt));
      }
      if (
        option &&
        (Array.isArray(option)
          ? option.length > 0
          : Object.keys(option).length > 0)
      ) {
        form.append("option", JSON.stringify(option));
      }
      if (extraEinvoiceData && Object.keys(extraEinvoiceData).length > 0) {
        form.append("einvoice_data", JSON.stringify(extraEinvoiceData));
      }
      form.append("cs_ids", selectedCsIds);
      if (tinNo) {
        form.append("tin_no", tinNo);
      }
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/confirmCheckout`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error processing checkout:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      return {
        status: false,
        message: error.response?.data?.message || "Failed to process checkout",
      };
    }
  }

  async calculateSelectedCartItems(itemIds, pricingType, csIds) {
    try {
      const form = new URLSearchParams();
      form.append("item_ids", itemIds);
      form.append("pricing_type", pricingType);
      form.append("cs_ids", csIds);

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/calculateCartItems`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error calculating selected items:", error);
      return { status: false, message: "Failed to calculate totals" };
    }
  }

  async addToCart(cartData) {
    try {
      const formData = new URLSearchParams();
      Object.keys(cartData).forEach((key) => {
        formData.append(key, cartData[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/addToCart`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Add to cart API error:", error);
      throw error;
    }
  }

  async updateCartQuantity(id, quantity) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("id", id);
    form.append("quantity", quantity);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/cart/updateQuantity`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating cart quantity:", error);
      return { status: false, message: "Failed to update quantity" };
    }
  }

  async removeFromCart(id) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("id", id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/removeCartItem`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing item from cart:", error);
      return { status: false, message: "Failed to remove item" };
    }
  }

  async removeBuyNowCart() {
    if (!localStorage.getItem("access_token")) {
      return { status: false, message: "Not logged in" };
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/removeBuyNowCart`,
        new URLSearchParams(),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing buy now cart items:", error);
      return { status: false, message: "Failed to remove buy now cart items" };
    }
  }

  async searchTinById(id_type, id_no) {
    try {
      const form = new URLSearchParams();
      form.append("id_type", id_type);
      form.append("id_no", id_no);

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/searchTin`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error searching TIN:", error);
      return { success: false, message: "Failed to search TIN" };
    }
  }

  async fpxPaid(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === "object") {
        form.append(key, JSON.stringify(value));
      } else {
        form.append(key, value);
      }
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/fpxPaid`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("FPX payment API error:", error);
      throw error;
    }
  }

  async checkingIsIncart(item_id) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("item_id", item_id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/checkingIsIncart`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error checking item in cart:", error);
      return { status: false, message: "Failed to check item in cart" };
    }
  }

  async getCartByPricingType() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/getCartByPricingType`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching cart item count:", error.response || error);
      return { status: false, message: "Failed to fetch cart item count" };
    }
  }

  async editCartItemBranch(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/cart/editCartItemBranch`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error editint cart item branch:", error.response || error);
      return { status: false, message: "Failed to edit cart item branch" };
    }
  }

  async getCreditLimit() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/cart/checkCreditLimit`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //================API for Cart Page END=================

  //=================API for Wishlist/Favorites Page=================
  async getMyWishlist(page = 1, limit = 5, search = "") {
    try {
      const form = new URLSearchParams();
      form.append("page", page);
      form.append("limit", limit);
      if (search && search.trim() !== "") {
        form.append("search", search);
      }

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getWishlist`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching Wish list:", error);
      return { status: false, message: "Failed to fetch Wish list" };
    }
  }

  async getMyFavourite(page = 1, limit = 5, search = "") {
    try {
      const form = new URLSearchParams();
      form.append("page", page);
      form.append("limit", limit);
      if (search && search.trim() !== "") {
        form.append("search", search);
      }

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getFavorite`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching Favorite:", error);
      return { status: false, message: "Failed to fetch Favorite" };
    }
  }

  async getMyRepeatOrder(page = 1, limit = 5, search = "") {
    try {
      const form = new URLSearchParams();
      form.append("page", page);
      form.append("limit", limit);
      if (search && search.trim() !== "") {
        form.append("search", search);
      }

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getRepeatOrder`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching Wish list:", error);
      return { status: false, message: "Failed to fetch Wish list" };
    }
  }

  async checkWishlistStatus(statusData = {}) {
    try {
      const formData = new URLSearchParams();
      Object.keys(statusData).forEach((key) => {
        formData.append(key, statusData[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/checkWishlistStatus`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Check wishlist status API error:", error);
      throw error;
    }
  }

  async addToWishlist(wishlistData) {
    try {
      const formData = new URLSearchParams();
      // ensure is_bundle always sent (default '0')
      const data = {
        ...wishlistData,
        is_bundle:
          wishlistData.is_bundle !== undefined
            ? String(wishlistData.is_bundle)
            : "0",
      };
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/addWishlist`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Add to wishlist API error:", error);
      return {
        status: 400,
        message: error.response?.data?.message || "Failed to add to wishlist",
      };
    }
  }

  async addToFavorite(favouriteData) {
    try {
      const formData = new URLSearchParams();
      // ensure is_bundle always sent (default '0')
      const data = {
        ...favouriteData,
        is_bundle:
          favouriteData.is_bundle !== undefined
            ? String(favouriteData.is_bundle)
            : "0",
      };
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/addFavorite`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Add to Favorite API error:", error);
      throw error;
    }
  }

  async checkFavoriteStatus(statusData = {}) {
    try {
      const formData = new URLSearchParams();
      Object.keys(statusData).forEach((key) => {
        formData.append(key, statusData[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/checkFavoriteStatus`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Check favorite status API error:", error);
      throw error;
    }
  }

  async removeFromWishlist(data) {
    try {
      const form = new URLSearchParams();
      // support removal by wishlist_id or by item_id + is_bundle
      if (data.wishlist_id) {
        form.append("wishlist_id", data.wishlist_id);
      } else if (data.item_id) {
        form.append("item_id", data.item_id);
        form.append(
          "is_bundle",
          data.is_bundle !== undefined ? String(data.is_bundle) : "0",
        );
      }
      form.append("pricing_type", data.pricing_type || "1");

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/removeWishlist`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing item from wishlist:", error);
      return {
        status: 400,
        message:
          error.response?.data?.message ||
          "Failed to remove item from wishlist",
      };
    }
  }

  async removeFromFavorite(data) {
    try {
      const form = new URLSearchParams();
      // Support removal by favorite_id (existing) or by item_id + is_bundle
      if (data.favorite_id) {
        form.append("favorite_id", data.favorite_id);
      } else if (data.item_id) {
        form.append("item_id", data.item_id);
        form.append(
          "is_bundle",
          data.is_bundle !== undefined ? String(data.is_bundle) : "0",
        );
      }
      form.append("pricing_type", data.pricing_type || "1");

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/removeFavorite`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error removing item from favorites:", error);
      return {
        status: 400,
        message:
          error.response?.data?.message ||
          "Failed to remove item from favorites",
      };
    }
  }

  async getCountAvailableWishlistItem(wishlistData) {
    try {
      const formData = new URLSearchParams();
      Object.keys(wishlistData).forEach((key) => {
        formData.append(key, wishlistData[key]);
      });

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/home/getCountAvailableWishlistItem`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Get wishlist count API error:", error);
      throw error;
    }
  }

  //=================API for Wishlist/Favorites Page END=================

  //=================API for Home Page=================
  async getAllBanner() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getAllBanner`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getMemo() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getMemo`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getAllBar() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getAllBar`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getAllPoster() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getAllPoster`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getAllCategory() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getAllCategory`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getAllBrand() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getBrands`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async getBrandsServiceCentre(product_group_code, state, city) {
    const params = new URLSearchParams();
    if (product_group_code)
      params.append("product_group_code", product_group_code);
    if (state) params.append("state", state);
    if (city) params.append("city", city);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getBrandsServiceCentre`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getServiceCentreStates(brand_id, product_group_code) {
    const params = new URLSearchParams();
    params.append("brand_id", brand_id);
    if (product_group_code)
      params.append("product_group_code", product_group_code);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getServiceCentreStates`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getServiceCentreCities(brand_id, state, product_group_code) {
    const params = new URLSearchParams();
    params.append("brand_id", brand_id);
    params.append("state", state);
    if (product_group_code)
      params.append("product_group_code", product_group_code);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getServiceCentreCities`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getPromotionEvent(event_id, offset = 0, limit = 20) {
    const params = new URLSearchParams();
    params.append("event_id", event_id);
    params.append("offset", offset);
    params.append("limit", limit);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getPromotionEvent`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );

    return response.data || [];
  }

  async getNewArrivalPromotionCount() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getNewArrivalPromotionCount`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getAllStatusCount() {
    const form = new URLSearchParams();

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/Home/getAllCounts`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getTutorialStatus() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getUnityTutorialStatus`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async updateTutorialStatus() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/updateUnityTutorialStatus`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //=================API for Home Page END=================

  //================API for Credit Note Page=================
  async getCreditTransactionHistory(params = {}) {
    const form = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/Credit_note/getCreditTransactionHistory`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getCreditNoteDetail(params = {}) {
    const form = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/Credit_note/getCreditNoteDetail`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }
  //================API for Credit Note Page END=================

  //================API for Profile Page=================
  async editUserName(username, sub_id = "") {
    const form = new URLSearchParams();
    form.append("username", username);
    if (sub_id) form.append("sub_id", sub_id);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/editUserName`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async editPhoneNo(phoneNo) {
    const form = new URLSearchParams();
    form.append("phone_no", phoneNo);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/editPhoneNo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async editProfilePic(formData) {
    const res = await axios.post(
      `${API_BASE_URL}api/frontend/profile/editProfilePic`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        withCredentials: true,
      },
    );
    return res.data;
  }

  async changePassword(old_password, new_password, sub_id = "") {
    const form = new URLSearchParams();
    form.append("old_password", old_password);
    form.append("new_password", new_password);
    if (sub_id) form.append("sub_id", sub_id);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/changePassword`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getCompanyInfo() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/getCompanyInfo`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  async searchBranchInfo(params = {}) {
    const form = new URLSearchParams();
    if (params.search) form.append("search", params.search);
    form.append("page", params.page || 1);
    form.append("limit", params.limit || 5);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/branchList`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async addBranchInfo(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/addBranchInfo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async editBranchInfo(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/editBranchInfo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async searchBankInfo(params = {}) {
    const form = new URLSearchParams();
    if (params.search) form.append("search", params.search);
    form.append("page", params.page || 1);
    form.append("limit", params.limit || 5);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/getBankInfo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async addBankInfo(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/addBankInfo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async editBankInfo(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/editBankInfo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async searchSubUserInfo(params = {}) {
    const form = new URLSearchParams();
    if (params.search) form.append("search", params.search);
    form.append("page", params.page || 1);
    form.append("limit", params.limit || 5);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/getCustomerSubAccount`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async addUserInfo(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === "object") {
        form.append(key, JSON.stringify(value));
      } else {
        form.append(key, value);
      }
    });
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/addSubUser`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async editUserInfo(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value) || typeof value === "object") {
        form.append(key, JSON.stringify(value));
      } else {
        form.append(key, value);
      }
    });
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/editSubUser`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async deleteSubUser(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/deleteSubUser`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async setDefaultBank(params = {}) {
    const form = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      form.append(key, value);
    });

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/setDefaultBank`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //================API for Profile Page END=================

  //================API for Location=================
  async getAllStates() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/getStates`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data?.states || [];
  }

  async getCitiesByState(state) {
    const form = new URLSearchParams();
    form.append("state", state);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/getCitiesByState`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data?.cities || [];
  }

  async getPostcodeByCity(city) {
    const form = new URLSearchParams();
    form.append("city", city);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/profile/getPostcodeByCity`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data?.postcodes || [];
  }
  //================API for Location END=================

  //================API for Repair Page=================
  async getRepairList(params = {}) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();

    if (params.page) form.append("page", params.page);
    if (params.search) form.append("search", params.search);
    if (params.status_type) form.append("status_type", params.status_type);
    if (params.limit) form.append("limit", params.limit);
    if (params.branch_id) form.append("branch_id", params.branch_id);
    if (params.date_from) form.append("date_from", params.date_from);
    if (params.date_to) form.append("date_to", params.date_to);

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/myRepair/getRepairByStatus`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getRepairDetails(params = {}) {
    const form = new URLSearchParams();

    if (params.repair_no) form.append("repair_no", params.repair_no);

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/myRepair/getRepairDetails`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async cancelRequest(params = {}) {
    const form = new URLSearchParams();

    if (params.repair_no) form.append("repair_no", params.repair_no);

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/myRepair/cancelRequest`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getTrackingDetail(params = {}) {
    const form = new URLSearchParams();

    if (params.repair_id) form.append("repair_id", params.repair_id);

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/myRepair/getTrackingDetail`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getRepairStatusCounts() {
    const form = new URLSearchParams();

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/myRepair/getRepairStatusCounts`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getItemCode(keyword) {
    try {
      const formData = new URLSearchParams();
      formData.append("keyword", keyword);

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/myRepair/getItemCode`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data?.data || [];
    } catch (error) {
      console.error("Error searching items:", error);
      throw error;
    }
  }

  async addNewRepair(formData) {
    try {
      // Log the data being sent for debugging
      //console.log("Form data being sent:", Object.fromEntries(formData));

      const response = await axios.post(
        // Fix the endpoint to match the backend
        `${API_BASE_URL}api/frontend/myRepair/addNewRepair`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("API error details:", error.response || error);
      throw error;
    }
  }
  //================API for Repair Page END=================

  //================API for Order Page=================
  async getOrderList(params = {}) {
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.search) form.append("search", params.search);
    if (params.branch_id) form.append("branch_id", params.branch_id);
    if (params.statusType) form.append("statusType", params.statusType);
    if (params.start_date) form.append("start_date", params.start_date);
    if (params.end_date) form.append("end_date", params.end_date);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/order/getOrderByStatus`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getAllRefund(params = {}) {
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.search) form.append("search", params.search);
    if (params.branch_id) form.append("branch_id", params.branch_id);
    if (params.start_date) form.append("start_date", params.start_date);
    if (params.end_date) form.append("end_date", params.end_date);
    if (params.status) form.append("status", params.status);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/myRefund/getAllRefund`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getBranchList(params = {}) {
    const form = new URLSearchParams();
    //if (params.branch_id) form.append("branch_id", params.branch_id);
    if (params.region) form.append("region", params.region);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/order/getBranchList`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data?.data || [];
  }

  async getBranchListWithRegions(params = {}) {
    const form = new URLSearchParams();
    if (params.bundle_id) form.append("bundle_id", params.bundle_id);
    else if (params.item_id) form.append("item_id", params.item_id);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/order/getBranchList`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return {
      data: response.data?.data || [],
      regions: response.data?.regions || [],
      regionAvailability: response.data?.region_availability || null,
    };
  }

  async getOrderStatusCount(params = {}) {
    const form = new URLSearchParams();
    if (params.pricing_type) form.append("pricing_type", params.pricing_type);

    // 支持 branch_id 为数组或字符串
    if (params.branch_id) {
      if (Array.isArray(params.branch_id)) {
        params.branch_id.forEach((id) => form.append("branch_id[]", id));
      } else {
        form.append("branch_id", params.branch_id);
      }
    }

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/order/getOrderStatusCount`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data?.data || {};
  }

  async confirmReceive(order_no) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    form.append("so_id", order_no);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/order/confirmReceive`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getOrderDetails(id) {
    const form = new URLSearchParams();
    form.append("so_id", id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/order/getOrderDetails`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order details:", error);
      throw error;
    }
  }

  async getOrderTrackingDetail(order_no) {
    const form = new URLSearchParams();
    form.append("so_id", order_no);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/order/getOrderTracking`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order tracking details:", error);
      throw error;
    }
  }

  async cancelOrders(orderIds, cancel_by = "", cancel_reason = "") {
    const form = new URLSearchParams();

    // Convert to string for single orders
    if (Array.isArray(orderIds)) {
      form.append("so_ids", orderIds.join(","));
    } else {
      form.append("so_ids", orderIds);
    }
    if (cancel_by) form.append("cancel_by", cancel_by);
    if (cancel_reason) form.append("cancel_reason", cancel_reason);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/order/cancelOrders`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error in cancelOrders API call:", error);
      if (error.response) {
        // Log detailed error information
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  }

  async getRefundDetail(refundNoteNo) {
    const form = new URLSearchParams();
    form.append("rn_no", refundNoteNo);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/myRefund/getRefundDetail`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching refund details:", error);
      return {
        status: error.response?.status || 500,
        message: error.response?.data?.message || "An error occurred",
      };
    }
  }

  async getOrderPriceSummary(params = {}) {
    const form = new URLSearchParams();
    if (params.so_ids) form.append("so_ids", params.so_ids);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/order/getOrderPriceSummary`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //===========API for Order Page END============

  //==========API for PayLater Page=============
  async getTermInvList(params = {}) {
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.search) form.append("search", params.search);
    if (params.status_type) form.append("status_type", params.status_type);
    if (params.branch_id) {
      if (Array.isArray(params.branch_id)) {
        // 多个分行 id，全部 append
        params.branch_id.forEach((id) => form.append("branch_id[]", id));
      } else {
        form.append("branch_id", params.branch_id);
      }
    }
    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/PayLater/getInvoiceByStatus`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getInvoiceDetail(invoice_id) {
    const form = new URLSearchParams();
    form.append("inv_no", invoice_id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/getInvoiceDetail`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      throw error;
    }
  }

  async getTotalAmount(params = {}) {
    const form = new URLSearchParams();
    // 支持 branch_id 为数组或字符串
    if (params.branch_id) {
      if (Array.isArray(params.branch_id)) {
        params.branch_id.forEach((id) => form.append("branch_id[]", id));
      } else {
        form.append("branch_id", params.branch_id);
      }
    }
    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/PayLater/getTotalInvAmount`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getTermPaymentHistory(params = {}) {
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.limit) form.append("limit", params.limit);

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/PayLater/getAllTermPaymentHistory`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getTermPaymentHistoryDetail(or_no) {
    const form = new URLSearchParams();
    form.append("or_no", or_no);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/getTermPaymentHistoryDetail`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching transaction details:", error);
      throw error;
    }
  }

  async getInvoiceInCart(params = {}) {
    const form = new URLSearchParams();
    form.append("page", params.page || 1);
    form.append("limit", params.limit || 5);

    // 支持 branch_id 为数组或字符串
    if (params.branch_id) {
      if (Array.isArray(params.branch_id)) {
        params.branch_id.forEach((id) => form.append("branch_id[]", id));
      } else {
        form.append("branch_id", params.branch_id);
      }
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/getInvoiceInCart`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching invoice cart:", error);
      throw error;
    }
  }

  async deletePaymentCart(params) {
    const form = new URLSearchParams();
    form.append("selected_id", params.selected_id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/deletePaymentCart`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting from payment cart:", error);
      throw error;
    }
  }

  async updatePaymentCart(params) {
    const form = new URLSearchParams();
    form.append("selected_id", params.selected_id);
    form.append("pay_amount", params.pay_amount);
    if (params.cn_id) {
      form.append("cn_id", params.cn_id);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/updatePaymentCart`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating payment cart:", error);
      throw error;
    }
  }

  async addInvoiceToCart(params) {
    const form = new URLSearchParams();
    form.append("selected_id", params.selected_id);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/addInvoiceToCart`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding invoice to cart:", error);
      throw error;
    }
  }

  async payByChequeIBG(params) {
    const form = new URLSearchParams();
    form.append("selected_ids", params.selected_ids);
    form.append("selected_so", params.selected_so);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/payByChequeIBG`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payment details:", error);
      throw error;
    }
  }

  async getFpxPaymentLink(bank_type) {
    const form = new URLSearchParams();
    form.append("bank_type", bank_type);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/fpxPaymentLink`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching fpx transaction details:", error);
      throw error;
    }
  }

  async getInvoiceStatusCount() {
    const form = new URLSearchParams();

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/PayLater/getInvoiceDueCount`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //==========API for PayLater Page END==========

  //==========API for Debit Note Page=================
  async getTotalDebitNoteAmount(params = {}) {
    const form = new URLSearchParams();
    if (params.branch_id) {
      if (Array.isArray(params.branch_id)) {
        params.branch_id.forEach((id) => form.append("branch_id[]", id));
      } else {
        form.append("branch_id", params.branch_id);
      }
    }
    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/DebitNote/getTotalDebitNoteAmount`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getAllDebitNote(params = {}) {
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.search) form.append("search", params.search);
    if (params.status_type) form.append("status_type", params.status_type);
    if (params.branch_id) {
      if (Array.isArray(params.branch_id)) {
        params.branch_id.forEach((id) => form.append("branch_id[]", id));
      } else {
        form.append("branch_id", params.branch_id);
      }
    }
    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/DebitNote/getAllDebitNote`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getDebitNoteDetail(dn_no) {
    const form = new URLSearchParams();
    form.append("dn_no", dn_no);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/DebitNote/getDebitNoteDetail`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      throw error;
    }
  }

  async getDebitStatusCount() {
    const form = new URLSearchParams();

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/DebitNote/getDebitNoteDueCount`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //==========API for Debit Note Page END=================

  //==========API for Contra Page=================
  async getAllContra(params = {}) {
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.search) form.append("search", params.search);
    if (params.start_date) form.append("start_date", params.start_date);
    if (params.end_date) form.append("end_date", params.end_date);
    if (params.limit) form.append("limit", params.limit);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/MyContra/getAllContra`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getContraDetail(contra_id) {
    const form = new URLSearchParams();
    form.append("contra_id", contra_id);
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/MyContra/getContraDetail`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }
  //==========API for Contra Page END=================

  //==========API for Confirm Payment Page===========
  async confirmPayByChequeIBGTerm(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/confirmPayByChequeIBGTerm`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );
      return response;
    } catch (error) {
      console.error("Error confirming payment:", error);
      throw error;
    }
  }

  async confirmPayByCN(params) {
    const form = new URLSearchParams();
    form.append("inv_list", params.inv_list);

    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/PayLater/confirmPayByCN`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response; // 返回整个response
    } catch (error) {
      console.error("Error confirming payment:", error);
      throw error;
    }
  }

  async confirmPayByChequeIBGCBD(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/Order/confirmPayByChequeIBGCBD`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );
      return response;
    } catch (error) {
      console.error("Error confirming payment:", error);
      throw error;
    }
  }

  async confirmPaymentChecking(data) {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/confirmPaymentChecking`,
      new URLSearchParams(data),
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //==========API for Confirm Payment END===========

  //==========API for Payment History Page===========
  async getPaymentHistory(params = {}) {
    const token = localStorage.getItem("access_token");
    const form = new URLSearchParams();
    if (params.page) form.append("page", params.page);
    if (params.limit) form.append("limit", params.limit);
    if (params.search) form.append("search", params.search);
    if (params.type) form.append("type", params.type);
    if (params.start_date) form.append("start_date", params.start_date);
    if (params.end_date) form.append("end_date", params.end_date);

    const response = await axios.post(
      `${API_BASE_URL}/api/frontend/payment/getPaymentHistory`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getPaymentDetail(orId) {
    const form = new URLSearchParams();
    form.append("or_id", orId);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/getPaymentDetail`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async cancelOR(orId) {
    const form = new URLSearchParams();
    form.append("or_id", orId);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/cancelOr`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getUpdateORDetail(orId) {
    const form = new URLSearchParams();
    form.append("or_id", orId);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/getUpdateORDetail`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async updateOfficialReceipt({
    or_id,
    payment_type,
    bankin_date,
    payment_file,
    unityBank,
  }) {
    const formData = new FormData();
    formData.append("or_id", or_id);
    formData.append("payment_type", payment_type);
    formData.append("bankin_date", bankin_date);
    if (payment_file) formData.append("bank_slip", payment_file);
    formData.append("unityBank", unityBank);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/updateOfficialReceipt`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async countResubmitReceipt() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/countUnsuccessfulResubmit`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async getSOId(do_id) {
    const form = new URLSearchParams();
    form.append("do_id", do_id);

    const response = await axios.post(
      `${API_BASE_URL}api/frontend/payment/getSoIdByDoId`,
      form,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      },
    );
    return response.data;
  }

  //==========API for Payment History Page END===========

  //==========API for My Documents Page===========
  async getDocuments(params = {}) {
    try {
      const form = new URLSearchParams();
      if (params.document_type)
        form.append("document_type", params.document_type);
      if (params.page) form.append("page", params.page);
      if (params.limit) form.append("limit", params.limit);
      if (params.search) form.append("search", params.search);
      if (params.branch_id) form.append("branch_id", params.branch_id);
      if (params.start_date) form.append("start_date", params.start_date);
      if (params.end_date) form.append("end_date", params.end_date);
      const response = await axios.post(
        `${API_BASE_URL}/api/frontend/myDocument/getDocuments`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching documents:", error);
      return { status: 500, message: "Failed to fetch documents" };
    }
  }

  async getStatement(params = {}) {
    try {
      const form = new URLSearchParams();
      if (params.statement_date)
        form.append("statement_date", params.statement_date);
      if (params.pricing_type) form.append("pricing_type", params.pricing_type);
      if (params.type) form.append("type", params.type);

      const response = await axios.post(
        `${API_BASE_URL}/api/frontend/myDocument/getStatement`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error getting statement:", error);
      throw error;
    }
  }

  async getAllBank() {
    const response = await axios.post(
      `${API_BASE_URL}api/frontend/home/getAllBankList`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Required if API expects credentials
      },
    );
    return response.data;
  }

  //==========API for My Documents Page===========

  //==========API for My Notification Page===========
  async getNotificationCategories() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/frontend/notification/getNotificationsCategories`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notification categories:", error);
      return {
        status: false,
        message: "Failed to fetch notification categories",
      };
    }
  }

  async getNotificationList(params = {}) {
    try {
      const form = new URLSearchParams();
      if (params.page) form.append("page", params.page);
      if (params.cat_id) form.append("cat_id", params.cat_id);
      if (params.sub_cat_id) form.append("sub_cat_id", params.sub_cat_id);

      const response = await axios.post(
        `${API_BASE_URL}api/frontend/notification/getNotificationsList`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching notification list:", error);
      return { status: false, message: "Failed to fetch notification list" };
    }
  }

  //==========API for My Notification Page END===========

  //==========API for After Sales Service Page===========
  async getWarrantyInfo(sn, serialType = "1") {
    try {
      const form = new URLSearchParams();
      form.append("serial_no", sn);
      if (String(serialType) === "2") {
        form.append("serial_type", "2");
      }

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_serial_number`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching warranty info:", error);
      if (error.response?.data) {
        return error.response.data;
      }
      return { status: false, message: "Failed to fetch warranty info" };
    }
  }

  async checkRegisterStatus(sn, serialType = "1") {
    try {
      const form = new URLSearchParams();
      form.append("serial_no", sn);
      if (String(serialType) === "2") {
        form.append("serial_type", "2");
      }

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/check_registered`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        console.error("Error fetching register status:", error);
        return error.response.data;
      }
    }
  }

  // async checkSnStatus(formData) {
  //   try {
  //     const response = await axios.post(
  //       `${API_BASE_URL}api/warranty_1/check_correct_item_sn`,
  //       formData,
  //       {
  //         headers: {
  //           "Content-Type": "application/x-www-form-urlencoded",
  //         },
  //         withCredentials: true,
  //       },
  //     );
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error fetching sn status:", error.response || error);
  //     return { status: false, message: "Failed to fetch sn status" };
  //   }
  // }

  async registerWarranty(formData) {
    try {
      const isFormData =
        typeof FormData !== "undefined" && formData instanceof FormData;
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/register`,
        isFormData ? formData : new URLSearchParams(formData),
        {
          headers: isFormData
            ? {}
            : {
                "Content-Type": "application/x-www-form-urlencoded",
              },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error registering warranty:", error.response || error);
      return { status: false, message: "Failed to register warranty" };
    }
  }

  async checkUnitsBundle(units) {
    try {
      const form = new URLSearchParams();
      form.append("serial_list", JSON.stringify(units));

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/check_units`,
        form,
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data) {
        return error.response.data;
      }
      return { status: false, message: "Failed to check units" };
    }
  }

  async checkTicketStatus(formData) {
    try {
      const payload =
        formData instanceof URLSearchParams
          ? formData
          : new URLSearchParams(formData);
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/check_ticket`,
        payload,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching ticket status:", error);
      return { status: false, message: "Failed to fetch ticket status" };
    }
  }

  async createTicket(formData) {
    try {
      const payload =
        formData instanceof URLSearchParams
          ? formData
          : new URLSearchParams(formData);
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/create_ticket`,
        payload,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating ticket:", error.response || error);
      return { status: false, message: "Failed to create ticket" };
    }
  }

  async getProductInfo(itemCode) {
    try {
      const form = new URLSearchParams();
      form.append("main_item_code", itemCode);

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_product_infomation`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching product info:", error.response || error);
      return { status: false, message: "Failed to fetch product info" };
    }
  }

  async getUnsolvedTicketCount(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_unsolved_ticket_count`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching ticket count:", error.response || error);
      return { status: false, message: "Failed to fetch ticket count" };
    }
  }

  async getTicketList(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_ticket_list`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching ticket list:", error.response || error);
      return { status: false, message: "Failed to fetch ticket list" };
    }
  }

  async getSingleTicket(formData) {
    try {
      const payload =
        formData instanceof URLSearchParams
          ? formData
          : new URLSearchParams(formData);
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_single_ticket`,
        payload,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching single ticket:", error.response || error);
      return { status: false, message: "Failed to fetch single ticket" };
    }
  }

  // Verify technician access code
  async verifyCodeCheck(verify_code, serial_no, type, serialType = "1") {
    try {
      const form = new URLSearchParams();
      form.append("verify_code", verify_code);
      form.append("serial_no", serial_no);
      form.append("type", type);
      if (String(serialType) === "2") {
        form.append("serial_type", "2");
      }
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/verify_code_check`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error verifying code:", error?.response || error);
      return { status: false, message: "Failed to verify code" };
    }
  }

  // Save technician detail (supports multipart form data for photo)
  async saveTechnicianDetail(formData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/save_technician_detail`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error saving technician detail:",
        error?.response || error,
      );
      return { status: false, message: "Failed to save technician detail" };
    }
  }

  async getServiceReason(serial_no, serialType = "1") {
    try {
      const form = new URLSearchParams();
      form.append("serial_no", serial_no);
      if (String(serialType) === "2") {
        form.append("serial_type", "2");
      }
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_service_reason`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching service reason:", error?.response || error);
      return { status: false, message: "Failed to fetch service reason" };
    }
  }

  async checkRepairRegisterStatus(ticket_no) {
    try {
      const form = new URLSearchParams();
      form.append("ticket_no", ticket_no);
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/check_repair_registered`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching repair register status:",
        error?.response || error,
      );
      return error.response.data;
    }
  }

  async updatePIN(serial_no, pin_code, serialType = "1") {
    try {
      const form = new URLSearchParams();
      form.append("serial_no", serial_no);
      form.append("pin_code", pin_code);
      if (String(serialType) === "2") {
        form.append("serial_type", "2");
      }
      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/update_pin_code`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating pin code:", error?.response || error);
      return error.response.data;
    }
  }

  async getErrorCode(item_id, language = "en") {
    try {
      const form = new URLSearchParams();
      form.append(
        "item_id",
        Array.isArray(item_id) ? JSON.stringify(item_id) : item_id,
      );
      form.append("language", language);

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_error_code`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching error code:", error?.response || error);
      return { status: false, message: "Failed to fetch error code" };
    }
  }

  async getUserManual(item_id, language = "en", section_type = 1) {
    try {
      const form = new URLSearchParams();
      form.append(
        "item_id",
        Array.isArray(item_id) ? JSON.stringify(item_id) : item_id,
      );
      form.append("language", language);
      form.append("section_type", section_type);

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_user_manual`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user manual:", error?.response || error);
      return { status: false, message: "Failed to fetch user manual" };
    }
  }

  async getWarrantyPolicy(item_id, language = "en") {
    try {
      const form = new URLSearchParams();
      form.append(
        "item_id",
        Array.isArray(item_id) ? JSON.stringify(item_id) : item_id,
      );
      form.append("language", language);

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_warranty_policy`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching warranty policy:", error?.response || error);
      return { status: false, message: "Failed to fetch warranty policy" };
    }
  }

  async getWarrantyStatus(item_id, language = "en") {
    try {
      const form = new URLSearchParams();
      form.append(
        "item_id",
        Array.isArray(item_id) ? JSON.stringify(item_id) : item_id,
      );
      form.append("language", language);

      const response = await axios.post(
        `${API_BASE_URL}api/warranty_1/get_warranty_status`,
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching warranty status:", error?.response || error);
      return { status: false, message: "Failed to fetch warranty status" };
    }
  }

  //==========API for After Sales Service END===========
}
// ✅ Assign instance to a variable
const apiServiceInstance = new ApiService();

export default apiServiceInstance;
